import React, { useState, useEffect, useContext, useMemo } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import axios from "axios";

import { Chart as ChartJS, registerables } from "chart.js";
import { Line } from "react-chartjs-2";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { AccountsContext } from "context/AccountsContext";
import DateRangePicker from "layouts/DateTimerange/dateTimeRange"; // same custom range picker used across all other reports
import DhundhooLoader from "../Reports/Loader/Loader";

ChartJS.register(...registerables);

// Same EV report API used by the EV Report page (POST, JSON when an interval is passed).
const EV_REPORT_API = "https://v3.dhundhoo.com/api/evdata/report";

const INTERVAL_OPTIONS = [
  { label: "5 min", value: 5 },
  { label: "10 min", value: 10 },
  { label: "15 min", value: 15 },
  { label: "20 min", value: 20 },
  { label: "25 min", value: 25 },
  { label: "30 min", value: 30 },
  { label: "35 min", value: 35 },
  { label: "40 min", value: 40 },
  { label: "45 min", value: 45 },
  { label: "50 min", value: 50 },
  { label: "55 min", value: 55 },
  { label: "60 min", value: 60 },
];

// Keys that identify the record / vehicle rather than a plottable parameter.
// Any key that normalizes to one of these (case/underscore/space insensitive) is an identifier,
// not a plottable parameter, so it never shows up in the graph's dimension picker.
const IDENTIFIER_KEY_NAMES = new Set([
  "thingid",
  "id",
  "deviceid",
  "device",
  "imei",
  "vehicleid",
  "vehicle",
  "orgid",
  "accountid",
  "userid",
  "simno",
  "sim",
  "regno",
  "registrationno",
  "registrationnumber",
  "vehicleno",
  "vehiclenumber",
]);
const normalizeKey = (key) => key.replace(/[_\s]/g, "").toLowerCase();
const isIdentifierKey = (key) => IDENTIFIER_KEY_NAMES.has(normalizeKey(key));
const TIMESTAMP_CANDIDATES = [
  "timeStamp",
  "timestamp",
  "time_stamp",
  "updatedAt",
  "updated_at",
  "createdAt",
  "created_at",
  "dateTime",
  "recordedAt",
  "recorded_at",
];

const CHART_COLORS = [
  "#FB8C00",
  "#3B82F6",
  "#10B981",
  "#EF4444",
  "#8B5CF6",
  "#F59E0B",
  "#06B6D4",
  "#EC4899",
  "#84CC16",
  "#6366F1",
];

const checkedIcon = <CheckBoxIcon fontSize="small" />;
const uncheckedIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;

const focusFieldSx = {
  "& .MuiOutlinedInput-root": {
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#FB8C00",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#FB8C00",
  },
};

const parseNumeric = (value) => {
  if (value === null || value === undefined || value === "null") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const cleaned = value.replace(/,/g, "").trim();
    if (cleaned === "") return null;
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : null;
  }
  return null;
};

const formatHeader = (key) =>
  key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const detectTimestampKey = (keys) => {
  const match = TIMESTAMP_CANDIDATES.find((c) => keys.includes(c));
  if (match) return match;
  const timeLike = keys.find((k) => /time|date/i.test(k));
  return timeLike || keys[0];
};

const formatTimeLabel = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

function OBDDashboard() {
  const { fetchThings, things } = useContext(AccountsContext);

  const [vehicleId, setVehicleId] = useState("");
  const [intervalMin, setIntervalMin] = useState(5);

  const getTodayDateString = () => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, "0");
    const d = String(today.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const todayStr = getTodayDateString();
  const defaultStart = `${todayStr}T00:00`;
  const defaultEnd = `${todayStr}T23:59`;

  const [startTime, setStartTime] = useState(defaultStart);
  const [endTime, setEndTime] = useState(defaultEnd);

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorSB, setErrorSB] = useState(false);
  const [successSB, setSuccessSB] = useState(false);

  const [rawRows, setRawRows] = useState([]);
  const [timestampKey, setTimestampKey] = useState(null);
  const [availableParams, setAvailableParams] = useState([]);
  const [selectedParams, setSelectedParams] = useState([]);

  useEffect(() => {
    fetchThings();
  }, []);

  const openErrorSB = () => setErrorSB(true);
  const closeErrorSB = () => setErrorSB(false);
  const closeSuccessSB = () => setSuccessSB(false);

  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return "";
    const [datePart, timePart] = dateTimeStr.split("T");
    return `${datePart} ${timePart ? `${timePart}:00` : "00:00:00"}`;
  };

  const fetchDashboardData = async () => {
    if (!vehicleId) {
      setErrorMessage("Please select a vehicle.");
      openErrorSB();
      return;
    }
    if (!startTime || !endTime) {
      setErrorMessage("Please select a date range first.");
      openErrorSB();
      return;
    }

    setLoading(true);
    setErrorMessage("");
    try {
      const payload = {
        thingId: vehicleId,
        fromDateTime: formatDateTime(startTime),
        toDateTime: formatDateTime(endTime),
        intervalMinutes: intervalMin,
      };
      const token = localStorage.getItem("token");
      const config = {
        headers: { "x-dhundhoo-session": token, "Content-Type": "application/json" },
      };

      const response = await axios.post(EV_REPORT_API, payload, config);
      const data = response.data;

      let dataArray = [];
      if (data && data.content && Array.isArray(data.content)) {
        dataArray = data.content;
      } else if (Array.isArray(data)) {
        dataArray = data;
      } else if (data) {
        dataArray = [data];
      }

      if (!dataArray || dataArray.length === 0 || Object.keys(dataArray[0]).length === 0) {
        setRawRows([]);
        setAvailableParams([]);
        setTimestampKey(null);
        setErrorMessage("No data found for this vehicle and time range.");
        openErrorSB();
        setLoading(false);
        return;
      }

      const allKeys = Array.from(
        new Set(dataArray.reduce((acc, curr) => [...acc, ...Object.keys(curr)], []))
      );
      const tsKey = detectTimestampKey(allKeys);

      const paramKeys = allKeys.filter((key) => {
        if (key === tsKey) return false;
        if (isIdentifierKey(key)) return false;
        return dataArray.some((row) => parseNumeric(row[key]) !== null);
      });

      if (paramKeys.length === 0) {
        setRawRows([]);
        setAvailableParams([]);
        setTimestampKey(null);
        setErrorMessage("No plottable numeric parameters were found in the response.");
        openErrorSB();
        setLoading(false);
        return;
      }

      setRawRows(dataArray);
      setTimestampKey(tsKey);
      setAvailableParams(paramKeys);
      setSelectedParams((prev) => {
        const stillValid = prev.filter((p) => paramKeys.includes(p));
        return stillValid.length > 0 ? stillValid : paramKeys.slice(0, 2);
      });
      setSuccessSB(true);
    } catch (error) {
      console.error(error);
      setErrorMessage("Failed to fetch EV report data from API.");
      openErrorSB();
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setVehicleId("");
    setIntervalMin(5);
    setStartTime(defaultStart);
    setEndTime(defaultEnd);
    setRawRows([]);
    setTimestampKey(null);
    setAvailableParams([]);
    setSelectedParams([]);
    setErrorMessage("");
  };

  const chartData = useMemo(() => {
    if (!rawRows.length || !timestampKey || selectedParams.length === 0) {
      return { labels: [], datasets: [] };
    }
    const labels = rawRows.map((row) => formatTimeLabel(row[timestampKey]));
    const datasets = selectedParams.map((param, idx) => ({
      label: formatHeader(param),
      data: rawRows.map((row) => parseNumeric(row[param])),
      borderColor: CHART_COLORS[idx % CHART_COLORS.length],
      backgroundColor: CHART_COLORS[idx % CHART_COLORS.length],
      spanGaps: true,
      tension: 0.3,
      pointRadius: 2,
      borderWidth: 2,
    }));
    return { labels, datasets };
  }, [rawRows, timestampKey, selectedParams]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { position: "bottom" },
      tooltip: { mode: "index", intersect: false },
    },
    scales: {
      x: {
        ticks: { maxRotation: 45, minRotation: 45, autoSkip: true, maxTicksLimit: 15 },
        title: { display: true, text: "Time" },
      },
      y: {
        title: { display: true, text: "Value" },
        beginAtZero: true,
      },
    },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3} pb={0}>
                <MDTypography variant="h6" fontWeight="medium">
                  OBD Dashboard
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  Powered by the EV Report API — pick a vehicle, a time range and an interval, then
                  choose which parameters to plot.
                </MDTypography>
              </MDBox>

              <MDBox p={3} pb={0}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Autocomplete
                      disablePortal
                      options={things || []}
                      getOptionLabel={(option) => option.thingId || ""}
                      value={things?.find((t) => t.thingId === vehicleId) || null}
                      onChange={(event, newValue) => {
                        setVehicleId(newValue ? newValue.thingId : "");
                      }}
                      renderOption={(props, option) => (
                        <li
                          {...props}
                          key={option.thingId}
                          style={{ display: "flex", justifyContent: "space-between", width: "100%" }}
                        >
                          <span>{option.nickName}</span>
                          <span style={{ color: "#666" }}>{option.thingId}</span>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search or select vehicle"
                          fullWidth
                          size="small"
                          sx={focusFieldSx}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <Autocomplete
                      disableClearable
                      options={INTERVAL_OPTIONS}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      value={INTERVAL_OPTIONS.find((o) => o.value === intervalMin) || INTERVAL_OPTIONS[0]}
                      onChange={(e, nv) => setIntervalMin(nv ? nv.value : 5)}
                      renderInput={(params) => (
                        <TextField {...params} label="Interval" fullWidth size="small" sx={focusFieldSx} />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={4.6}>
                    <div style={{ width: "70%" }}>
                      <DateRangePicker
                        startTime={startTime}
                        endTime={endTime}
                        onChange={({ startTime: s, endTime: e }) => {
                          setStartTime(s);
                          setEndTime(e);
                        }}
                        disabled={loading}
                      />
                    </div>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <MDButton
                          fullWidth
                          variant="gradient"
                          color="warning"
                          size="small"
                          onClick={fetchDashboardData}
                          disabled={loading}
                        >
                          {loading ? <CircularProgress size={15} color="inherit" /> : "LOAD"}
                        </MDButton>
                      </Grid>
                      <Grid item xs={6}>
                        <MDButton
                          fullWidth
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={handleClear}
                          disabled={loading}
                        >
                          CLEAR
                        </MDButton>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                {availableParams.length > 0 && (
                  <Grid container spacing={2} mt={0.5}>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        options={availableParams}
                        getOptionLabel={(option) => formatHeader(option)}
                        value={selectedParams}
                        onChange={(event, newValue) => setSelectedParams(newValue)}
                        renderOption={(props, option, { selected }) => (
                          <li {...props}>
                            <Checkbox
                              icon={uncheckedIcon}
                              checkedIcon={checkedIcon}
                              checked={selected}
                              style={{ marginRight: 8 }}
                            />
                            {formatHeader(option)}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Parameters to plot"
                            placeholder="Select parameters"
                            fullWidth
                            size="small"
                            sx={focusFieldSx}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                )}
              </MDBox>

              {errorMessage && (
                <MDBox px={3} pt={2}>
                  <Alert severity="error" onClose={() => setErrorMessage("")}>
                    {errorMessage}
                  </Alert>
                </MDBox>
              )}

              <MDBox p={3} pt={2}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" p={5}>
                    <DhundhooLoader />
                  </MDBox>
                ) : chartData.datasets.length > 0 ? (
                  <MDBox
                    sx={{
                      height: { xs: "280px", sm: "340px", md: "400px", lg: "430px", xl: "480px" },
                      width: "100%",
                    }}
                  >
                    <Line data={chartData} options={chartOptions} />
                  </MDBox>
                ) : (
                  <MDBox display="flex" justifyContent="center" p={5}>
                    <MDTypography variant="button" color="text">
                      Select a vehicle and date range, then click LOAD to see the chart.
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <MDSnackbar
        color="success"
        icon="check"
        title="OBD Dashboard"
        content="Data loaded successfully."
        open={successSB}
        onClose={closeSuccessSB}
        close={closeSuccessSB}
        bgWhite
      />
      <MDSnackbar
        color="error"
        icon="warning"
        title="OBD Dashboard"
        content={errorMessage || "Failed to load data."}
        open={errorSB}
        onClose={closeErrorSB}
        close={closeErrorSB}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default OBDDashboard;